<template>
  <div class="compair-layout--box">
    <div class="compair-layout--box-header">
      <h3>Filter</h3>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.startDate"
            type="date"
            label="Von"
            name="from"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.endDate"
            type="date"
            label="Bis"
            name="to"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
      </div>
    </div>
    <div class="compair-layout--box-body">
      <template v-if="(googleIds || facebookIds) && datacollection">
        <FacebookStatChart
          ref="chart"
          :chart-data="datacollection"
          :height="250"
          :max="chartMaxY"
          :options="options"
        ></FacebookStatChart>
      </template>
      <template v-else> Keine Daten für diese Filter verfügbar. </template>
    </div>
  </div>
</template>
<script>
import gql from "graphql-tag";
import moment from "moment";
import DataInput from "@/components/ui/DataInput";
import FacebookStatChart from "@/components/bi/charts/FacebookStatChart";
import updateChartYLegendMixin from "@/mixins/updateChartYLegendMixin";

export default {
  components: {
    DataInput,
    FacebookStatChart
  },
  mixins: [updateChartYLegendMixin],
  props: {
    googleIds: {
      type: Array,
      default: () => []
    },
    facebookIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      datapoints: {
        google: [],
        facebook: []
      },
      filter: {
        campaignID: null,
        startDate: moment().subtract(7, "days"),
        endDate: null
      },
      datacollection: null
    };
  },
  apollo: {
    googleAdOverview: {
      query: gql`
        query GoogleAdOverview(
          $size: Int!
          $filter: GoogleAdFilter
          $datapointStart: Time!
          $datapointEnd: Time
        ) {
          googleAdOverview(size: $size, filter: $filter) {
            results {
              id
              datapoints(from: $datapointStart, to: $datapointEnd) {
                date
                clicks
                costMicros
                avgCpc
                impressions
                ctr
              }
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            campaignID: this.googleIds
          },
          datapointStart: this.filter.startDate,
          datapointEnd: this.filter.endDate,
          size: 1000
        };
      }
    },
    facebookAdOverview: {
      query: gql`
        query FacebookAdOverview(
          $size: Int!
          $filter: FacebookAdInsightFilter
          $datapointStart: Time!
          $datapointEnd: Time
        ) {
          facebookAdOverview(size: $size, filter: $filter) {
            results {
              id
              datapoints(from: $datapointStart, to: $datapointEnd) {
                date
                clicks
                spend
                cpC
                impressions
                ctr
              }
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            adIDs: this.facebookIds
          },
          datapointStart: this.filter.startDate,
          datapointEnd: this.filter.endDate,
          size: 1000
        };
      }
    }
  },
  watch: {
    datapoints: {
      dep: true,
      handler(datapoints) {
        let stats = {
          datapoints: datapoints.google.concat(datapoints.faceboook)
        };
        stats.datapoints.sort((a, b) =>
          a.date > b.date ? 1 : a.date < b.date ? -1 : 0
        );

        let labels = [];
        for (let data of stats.datapoints) {
          let date = moment(data.date);
          if (labels.findIndex(val => val.diff(date) === 0) === -1) {
            labels.push(date);
          }
        }
        let maxValue = 1;
        let clicksData = [];
        let spendData = [];
        let cpCData = [];
        let ctrData = [];
        let impressionsData = [];
        for (let label of labels) {
          let clicks = 0;
          let spend = 0;
          let cpC = 0;
          let impressions = 0;
          let ctr = 0;
          for (let data of stats.datapoints) {
            let time = moment(data.date);
            if (time.diff(label) === 0) {
              clicks = data.clicks;
              if (data.costMicros || data.avgCpc) {
                spend = data.costMicros / 1000000;
                cpC = data.avgCpc / 1000000;
              } else if (data.spend || data.cpC) {
                spend = data.spend;
                cpC = data.cpC;
              }
              impressions = data.impressions;
              ctr = data.ctr;
            }
          }

          const currentMaxValue = Math.max(
            clicks,
            spend,
            cpC,
            impressions,
            ctr
          );
          if (currentMaxValue > maxValue) {
            maxValue = currentMaxValue;
          }

          clicksData.push(clicks);
          spendData.push(spend);
          cpCData.push(cpC);
          ctrData.push(ctr);
          impressionsData.push(impressions);
        }

        this.internalMaxY = maxValue;
        this.$emit("max-y-updated", maxValue);

        this.datacollection = {
          labels,
          datasets: [
            {
              label: "Klicks",
              borderColor: "#13358e",
              data: clicksData,
              fill: true,
              backgroundColor: "rgba(19, 53, 142, 0.8)",
              pointHoverBackgroundColor: "#000000",
              lineTension: 0.4
            },
            {
              label: "Ausgegeben",
              borderColor: "#55c400",
              data: spendData,
              fill: true,
              backgroundColor: "rgba(85, 196, 0, 0.8)",
              pointHoverBackgroundColor: "#000000",
              lineTension: 0.4
            },
            {
              label: "CPC",
              borderColor: "#d1232b",
              data: cpCData,
              fill: true,
              backgroundColor: "rgba(209, 35, 43, 0.8)",
              pointHoverBackgroundColor: "#000000",
              lineTension: 0.4
            },
            {
              label: "Impressionen",
              borderColor: "#48c6ef",
              data: impressionsData,
              fill: true,
              backgroundColor: "rgba(72, 198, 239, 0.8)",
              pointHoverBackgroundColor: "#000000",
              lineTension: 0.4
            },
            {
              label: "CTR",
              borderColor: "#48c6ef",
              data: ctrData,
              fill: true,
              backgroundColor: "rgba(72, 198, 239, 0.8)",
              pointHoverBackgroundColor: "#000000",
              lineTension: 0.4
            }
          ]
        };
      }
    },
    googleAdOverview(stats) {
      if (!stats.results || !stats.results.datapoints) {
        return;
      } else {
        let datapoints = [];
        for (let result of stats.results) {
          datapoints = datapoints.concat(result.datapoints);
        }
        this.datapoints.google = datapoints;
      }
    },
    facebookAdOverview(stats) {
      if (!stats.results || !stats.results.datapoints) {
        return;
      } else {
        let datapoints = [];
        for (let result of stats.results) {
          datapoints = datapoints.concat(result.datapoints);
        }
        this.datapoints.facebook = datapoints;
      }
    }
  }
};
</script>
